<script setup lang="ts">
import CodeInput from '@/widgets/codeInput/ui/index.vue';
import UIButton from '@/shared/ui/Button/index';
import PhoneInput from '@/shared/ui/PhoneInput/index';
import { PRIVACY_POLICY_LINK } from '@/shared/constants/documents';

import { useAuth } from '@/features/AuthForm/composables/useAuth';

const { t } = useI18n();

const {
  authTitle,
  authSubtitle,
  loginValue,
  codeValue,
  errorCode,
  isShowTimer,
  timerText,
  isConfirmationCode,
  errors,
  buttonText,
  isDisabledLoginButton,
  resetErrorCode,
  submitHandler,
} = useAuth();
</script>

<template>
  <div class="auth-form">
    <div class="auth-form__header">
      <div class="auth-form__title">{{ authTitle }}</div>
      <div class="auth-form__subtitle" v-html="authSubtitle"></div>
    </div>
    <div class="auth-form__form-wrapper">
      <form class="auth-form__form">
        <div class="auth-form__form-item">
          <PhoneInput
            v-if="!isConfirmationCode"
            v-model="loginValue"
            :error="errors[errorCode]"
            mask="+00 000-00-00"
            is-big-text
            @input="resetErrorCode"
          />
          <CodeInput
            v-else
            v-model="codeValue"
            autocomplete="one-time-code"
            :error="errors[errorCode]"
            :length="6"
            @input="resetErrorCode"
          />
        </div>
        <UIButton
          v-if="!isShowTimer"
          class="auth-form__button"
          type="submit"
          :aria-label="buttonText"
          :text="buttonText"
          :disabled="isDisabledLoginButton"
          @click="submitHandler"
        />
      </form>
      <div v-if="isConfirmationCode && isShowTimer" class="auth-form__timer">
        {{ timerText }}
      </div>
      <div v-if="!isConfirmationCode" class="auth-form__privacy">
        {{ t('authFormPrivacy') }}
        <a
          class="auth-form__link"
          :href="PRIVACY_POLICY_LINK"
          :aria-label="t('privacyPolicy')"
          target="_blank"
        >
          {{ t('authFormPrivacyLink') }}
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.auth-form {
  .flex-column(@size-x6);

  &__header {
    .flex-column(@size-x4);
  }

  &__title {
    font-size: @size-x7;
    font-weight: 700;
    line-height: 130%;
  }

  &__subtitle {
    font-size: @size-x4;
    font-weight: 500;
    line-height: 125%;
    color: @color-dove-gray-secondary;
  }

  &__form-wrapper {
    .flex-column(@size-x10);
  }

  &__form {
    .flex-column(@size-x6);
  }

  &__form-item {
    .flex-column(@size-x5);
  }

  &__privacy,
  &__timer {
    font-size: 14px;
    font-weight: 400;
    line-height: 130%;
    text-align: center;
  }

  &__link {
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  &__phone {
    color: @color-cod-gray;
  }

  @media @1024 {
    justify-content: center;

    &__header {
      gap: @size-x4;
    }

    &__title {
      font-size: @size-x6;
    }

    &__subtitle {
      font-size: 15px;
    }
  }
}
</style>
